/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useRef, useState } from 'react';

import { cssMap as cssMapCompiled, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

/* eslint-disable @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */
const compiledStyles = cssMapCompiled({
	container: {
		position: 'relative',
		isolation: 'isolate',
		overflow: 'hidden',
		borderRadius: '8px',
		transition: 'background-color 150ms ease-in-out',
	},
	/**
	 * Suppress ButtonItem's own background in all interaction states
	 * so the wrapper's background shows through.
	 */
	transparentButton: {
		'& button': {
			backgroundColor: 'transparent',
		},
		'& button:hover': {
			backgroundColor: 'transparent',
		},
		'& button:active': {
			backgroundColor: 'transparent',
		},
	},
	/**
	 * Override text and icon colors to white for the dark background.
	 */
	darkText: {
		'& span': {
			color: token('color.text.inverse'),
		},
		'& label': {
			color: token('color.text.inverse'),
		},
		'& svg': {
			color: token('color.text.inverse'),
		},
	},
	overlay: {
		position: 'absolute',
		inset: 0,
		zIndex: -1,
		borderRadius: '8px',
		pointerEvents: 'none',
	},
});
/* eslint-enable @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */

// Use token for glow color so it adapts to light/dark mode
const getGlow = () =>
	`radial-gradient(140px circle at var(--spotlight-x, 50%) var(--spotlight-y, 50%), ${token(
		'color.background.neutral.bold.hovered',
	)}, transparent 70%)`;

type TaskModeMenuItemBackgroundProps = {
	children: React.ReactNode;
	isSelected: boolean;
};

export const TaskModeMenuItemBackground = ({
	children,
}: TaskModeMenuItemBackgroundProps): JSX.Element => {
	const [isHovered, setIsHovered] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const handleMouseEnter = useCallback(() => {
		setIsHovered(true);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const rect = container.getBoundingClientRect();
		container.style.setProperty('--spotlight-x', `${e.clientX - rect.left}px`);
		container.style.setProperty('--spotlight-y', `${e.clientY - rect.top}px`);
	}, []);

	return (
		<div
			role="group"
			ref={containerRef}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={() => {}}
			onBlur={() => {}}
			onMouseMove={handleMouseMove}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ backgroundColor: token('color.background.neutral.bold') }}
			css={[compiledStyles.container, compiledStyles.transparentButton, compiledStyles.darkText]}
		>
			{/* Glow/spotlight overlay */}
			{isHovered && (
				<div
					css={compiledStyles.overlay}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ background: getGlow() }}
					aria-hidden="true"
				/>
			)}
			{children}
		</div>
	);
};
